import { useEffect, useState } from 'react';
import type React from 'react';

import { getWindowSafe } from '@change-corgi/core/window';

import { findScrollParent } from '#src/app/shared/utils/dom';

/**
 * True once `contentElRef`'s content overflows its nearest scrolling ancestor — e.g. to dock a footer,
 * or to give a scroll region a tab stop, only when scrolling is actually enabled.
 * Re-measures on any resize of the scroll container or the content (items loading in, the viewport
 * changing, the keyboard opening) via a ResizeObserver on both. `resetKey` re-runs the setup when the
 * observed element (re)appears — e.g. across a status change from loading to ready.
 */
export function useScrollContainerOverflow<T extends HTMLElement>(
	contentElRef: React.RefObject<T | null>,
	resetKey: string,
): boolean {
	const [isOverflowing, setIsOverflowing] = useState(false);
	useEffect(() => {
		const win = getWindowSafe();
		const contentEl = contentElRef.current;
		if (!win || !contentEl || typeof win.ResizeObserver !== 'function') return undefined;
		const scrollEl = findScrollParent(contentEl, win);
		if (!scrollEl) return undefined;
		const measure = (): void => setIsOverflowing(scrollEl.scrollHeight - scrollEl.clientHeight > 1);
		measure();
		const observer = new win.ResizeObserver(measure);
		observer.observe(scrollEl);
		observer.observe(contentEl);
		return () => observer.disconnect();
	}, [contentElRef, resetKey]);
	return isOverflowing;
}
